// File: frontend/src/components/admin/AdminSettings.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    FaGlobe,
    FaUserPlus,
    FaFileAlt,
    FaShieldAlt,
    FaComments,
    FaSearch,
    FaImage,
    FaKey,
    FaSave,
    FaCheck,
    FaSpinner,
    FaExclamationTriangle,
} from "react-icons/fa";
import {
    getAllSettings,
    batchUpdateSettings,
    toValueMap,
    SETTING_KEYS,
    SystemSettingDto,
} from "../../services/adminSettingsService";
import { AdminErrorBanner } from "../common/AdminBanner";
import { parseApiError } from "../../utils/parseApiError";
import { AdminSchedulers } from "./AdminSchedulers";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import "./AdminSettings.css";

interface AdminSettingsProps {
    isAdmin: boolean;
}

// ── Field definitions per group ───────────────────────────────────────────────

/** A text / number input field */
interface InputFieldDef {
    type:    "text" | "number";
    key:     string;
    labelKey: string; // i18n key
    hintKey?: string; // i18n key for hint text
    min?:    number;
    max?:    number;
    // Hard character cap for text fields. Also quoted in the hint, so the admin
    // reads the limit instead of discovering it when typing stops working.
    maxLength?: number;
    full?:   boolean; // span full row width
}

/** A boolean toggle field */
interface ToggleFieldDef {
    type:    "toggle";
    key:     string;
    labelKey: string;
    hintKey?: string;
}

/** A select / dropdown field */
interface SelectFieldDef {
    type:    "select";
    key:     string;
    labelKey: string;
    hintKey?: string;
    options: { value: string; label: string }[];
}

type FieldDef = InputFieldDef | ToggleFieldDef | SelectFieldDef;

interface GroupDef {
    id:       string;
    icon:     React.ReactNode;
    titleKey: string; // i18n key
    keys:     string[]; // setting keys belonging to this group
    fields:   FieldDef[];
}

const GROUPS: GroupDef[] = [
    {
        id:       "general",
        icon:     <FaGlobe />,
        titleKey: "admin_settings_group_general",
        keys: [
            SETTING_KEYS.SITE_NAME,
            SETTING_KEYS.SITE_SLOGAN,
            SETTING_KEYS.SITE_MAINTENANCE,
            SETTING_KEYS.SITE_ANNOUNCEMENT_ENABLED,
            SETTING_KEYS.SITE_ANNOUNCEMENT_TEXT,
        ],
        fields: [
            // The three text fields in this section were the only ones on the page
            // with no hint line, so nothing told the admin where each value shows up
            { type: "text",   key: SETTING_KEYS.SITE_NAME,                labelKey: "admin_settings_site_name",                 hintKey: "admin_settings_site_name_hint",         maxLength: 60 },
            { type: "text",   key: SETTING_KEYS.SITE_SLOGAN,              labelKey: "admin_settings_site_slogan",               hintKey: "admin_settings_site_slogan_hint",       maxLength: 120 },
            { type: "toggle", key: SETTING_KEYS.SITE_MAINTENANCE,         labelKey: "admin_settings_site_maintenance",          hintKey: "admin_settings_site_maintenance_hint" },
            { type: "toggle", key: SETTING_KEYS.SITE_ANNOUNCEMENT_ENABLED,labelKey: "admin_settings_site_announcement_enabled",  hintKey: "admin_settings_site_announcement_enabled_hint" },
            { type: "text",   key: SETTING_KEYS.SITE_ANNOUNCEMENT_TEXT,   labelKey: "admin_settings_site_announcement_text",    hintKey: "admin_settings_site_announcement_text_hint", maxLength: 500, full: true },
        ],
    },
    {
        id:       "registration",
        icon:     <FaUserPlus />,
        titleKey: "admin_settings_group_registration",
        keys: [
            SETTING_KEYS.REGISTRATION_ENABLED,
            SETTING_KEYS.DEFAULT_ROLE,
            SETTING_KEYS.USER_BIO_MAX_LENGTH,
            SETTING_KEYS.USERNAME_MAX_LENGTH,
            SETTING_KEYS.EMAIL_VERIFICATION,
        ],
        fields: [
            { type: "toggle", key: SETTING_KEYS.REGISTRATION_ENABLED,   labelKey: "admin_settings_registration_enabled",   hintKey: "admin_settings_registration_enabled_hint" },
            {
                type:     "select",
                key:      SETTING_KEYS.DEFAULT_ROLE,
                labelKey: "admin_settings_default_role",
                hintKey:  "admin_settings_default_role_hint",
                options:  [
                    { value: "ROOKIE", label: "ROOKIE" },
                    { value: "AUTHOR", label: "AUTHOR" },
                ],
            },
            { type: "number", key: SETTING_KEYS.USER_BIO_MAX_LENGTH,    labelKey: "admin_settings_bio_max_length",         hintKey: "admin_settings_bio_max_length_hint",       min: 10, max: 500 },
            // Username max length — min mirrors USERNAME_MIN_LENGTH, max is the
            // hard ceiling of 50 (snapshot DB columns are VARCHAR(50))
            { type: "number", key: SETTING_KEYS.USERNAME_MAX_LENGTH,    labelKey: "admin_settings_username_max_length",    hintKey: "admin_settings_username_max_length_hint",  min: 3,  max: 50 },
            { type: "toggle", key: SETTING_KEYS.EMAIL_VERIFICATION,     labelKey: "admin_settings_email_verification",     hintKey: "admin_settings_email_verification_hint" },
        ],
    },
    {
        // Own group rather than extra rows under "registration": the password
        // reset window applies to every account, verified or not, so it does not
        // belong in a section titled after sign-up.
        id:       "auth",
        icon:     <FaKey />,
        titleKey: "admin_settings_group_auth",
        keys: [
            SETTING_KEYS.AUTH_EMAIL_VERIFICATION_TOKEN_HOURS,
            SETTING_KEYS.AUTH_PASSWORD_RESET_TOKEN_HOURS,
            SETTING_KEYS.AUTH_VERIFICATION_RESEND_COOLDOWN_MINUTES,
        ],
        fields: [
            // Ranges mirror the clamps in SystemSettingService — a value outside
            // them would silently be pulled back, so the input refuses it upfront
            { type: "number", key: SETTING_KEYS.AUTH_EMAIL_VERIFICATION_TOKEN_HOURS,       labelKey: "admin_settings_auth_email_verification_token_hours",       hintKey: "admin_settings_auth_email_verification_token_hours_hint",       min: 1, max: 168 },
            { type: "number", key: SETTING_KEYS.AUTH_PASSWORD_RESET_TOKEN_HOURS,           labelKey: "admin_settings_auth_password_reset_token_hours",           hintKey: "admin_settings_auth_password_reset_token_hours_hint",           min: 1, max: 72 },
            { type: "number", key: SETTING_KEYS.AUTH_VERIFICATION_RESEND_COOLDOWN_MINUTES, labelKey: "admin_settings_auth_verification_resend_cooldown_minutes", hintKey: "admin_settings_auth_verification_resend_cooldown_minutes_hint", min: 0, max: 60 },
        ],
    },
    {
        id:       "entry",
        icon:     <FaFileAlt />,
        titleKey: "admin_settings_group_entry",
        keys: [
            SETTING_KEYS.ENTRY_MIN_LENGTH,
            SETTING_KEYS.ENTRY_MAX_LENGTH,
            SETTING_KEYS.ENTRY_DAILY_LIMIT,
            SETTING_KEYS.ENTRY_EDIT_WINDOW_MINUTES,
            SETTING_KEYS.ENTRY_DAILY_EDIT_LIMIT,
            SETTING_KEYS.ENTRY_DRAFT_LIMIT,
            SETTING_KEYS.ENTRY_PREVIEW_LINE_LIMIT,
            SETTING_KEYS.MOD_SPAM_COOLDOWN_SECONDS,
            SETTING_KEYS.TOPIC_TITLE_MAX_LENGTH,
            SETTING_KEYS.TOPIC_SUGGESTION_COOLDOWN_SECONDS,
            SETTING_KEYS.TOPIC_SUGGESTION_DAILY_LIMIT,
        ],
        fields: [
            { type: "number", key: SETTING_KEYS.ENTRY_MIN_LENGTH,           labelKey: "admin_settings_entry_min_length",         hintKey: "admin_settings_entry_min_length_hint",         min: 1,   max: 500 },
            { type: "number", key: SETTING_KEYS.ENTRY_MAX_LENGTH,           labelKey: "admin_settings_entry_max_length",         hintKey: "admin_settings_entry_max_length_hint",         min: 100, max: 100000 },
            { type: "number", key: SETTING_KEYS.ENTRY_DAILY_LIMIT,          labelKey: "admin_settings_entry_daily_limit",        hintKey: "admin_settings_entry_daily_limit_hint",        min: 0,   max: 1000 },
            { type: "number", key: SETTING_KEYS.ENTRY_EDIT_WINDOW_MINUTES,  labelKey: "admin_settings_entry_edit_window",        hintKey: "admin_settings_entry_edit_window_hint",        min: 0,   max: 10080 },
            // Daily edit quota — range mirrors NUMERIC_RANGES in SystemSettingService (0 = unlimited)
            { type: "number", key: SETTING_KEYS.ENTRY_DAILY_EDIT_LIMIT,     labelKey: "admin_settings_entry_daily_edit_limit",   hintKey: "admin_settings_entry_daily_edit_limit_hint",   min: 0,   max: 1000 },
            { type: "number", key: SETTING_KEYS.ENTRY_DRAFT_LIMIT,          labelKey: "admin_settings_entry_draft_limit",        hintKey: "admin_settings_entry_draft_limit_hint",        min: 1,   max: 200 },
            { type: "number", key: SETTING_KEYS.ENTRY_PREVIEW_LINE_LIMIT,    labelKey: "admin_settings_entry_preview_line_limit", hintKey: "admin_settings_entry_preview_line_limit_hint", min: 0,   max: 200 },
            { type: "number", key: SETTING_KEYS.MOD_SPAM_COOLDOWN_SECONDS,    labelKey: "admin_settings_spam_cooldown_seconds",    hintKey: "admin_settings_spam_cooldown_seconds_hint",    min: 0,   max: 3600 },
            // Topic title max length — allowed range 50-80 (backend clamps as well)
            { type: "number", key: SETTING_KEYS.TOPIC_TITLE_MAX_LENGTH,        labelKey: "admin_settings_topic_title_max_length",   hintKey: "admin_settings_topic_title_max_length_hint",   min: 50,  max: 80 },
            // Suggestion rate limits — defaults: 10 s cooldown, 500 per day (0 disables the check)
            { type: "number", key: SETTING_KEYS.TOPIC_SUGGESTION_COOLDOWN_SECONDS, labelKey: "admin_settings_topic_suggestion_cooldown", hintKey: "admin_settings_topic_suggestion_cooldown_hint", min: 0, max: 3600 },
            { type: "number", key: SETTING_KEYS.TOPIC_SUGGESTION_DAILY_LIMIT,      labelKey: "admin_settings_topic_suggestion_daily",    hintKey: "admin_settings_topic_suggestion_daily_hint",    min: 0, max: 10000 },
        ],
    },
    {
        id:       "moderation",
        icon:     <FaShieldAlt />,
        titleKey: "admin_settings_group_moderation",
        keys: [
            SETTING_KEYS.MOD_AUTO_BAN_THRESHOLD,
            SETTING_KEYS.MOD_REPORT_COOLDOWN_DAYS,
            SETTING_KEYS.MOD_DEFAULT_IP_BAN_DAYS,
        ],
        fields: [
            { type: "number", key: SETTING_KEYS.MOD_AUTO_BAN_THRESHOLD,    labelKey: "admin_settings_auto_ban_threshold",       hintKey: "admin_settings_auto_ban_threshold_hint",       min: 1,  max: 1000 },
            { type: "number", key: SETTING_KEYS.MOD_REPORT_COOLDOWN_DAYS,  labelKey: "admin_settings_report_cooldown_days",     hintKey: "admin_settings_report_cooldown_days_hint",     min: 0,  max: 365 },
            { type: "number", key: SETTING_KEYS.MOD_DEFAULT_IP_BAN_DAYS,   labelKey: "admin_settings_default_ip_ban_days",      hintKey: "admin_settings_default_ip_ban_days_hint",     min: 0,  max: 3650 },
        ],
    },
    {
        id:       "messaging",
        icon:     <FaComments />,
        titleKey: "admin_settings_group_messaging",
        keys: [
            SETTING_KEYS.MSG_MAX_LENGTH,
            SETTING_KEYS.MSG_INBOX_LIMIT,
            SETTING_KEYS.MSG_RATE_LIMIT_MAX_MESSAGES,
            SETTING_KEYS.MSG_RATE_LIMIT_WINDOW_SECONDS,
        ],
        fields: [
            { type: "number", key: SETTING_KEYS.MSG_MAX_LENGTH,   labelKey: "admin_settings_msg_max_length",   hintKey: "admin_settings_msg_max_length_hint",   min: 100, max: 10000 },
            // Inbox limit — 0 means unlimited (MessageService skips the capacity
            // check), so the lower bound is 0. Range mirrors NUMERIC_RANGES in
            // SystemSettingService; keep both sides in sync.
            { type: "number", key: SETTING_KEYS.MSG_INBOX_LIMIT,  labelKey: "admin_settings_msg_inbox_limit",  hintKey: "admin_settings_msg_inbox_limit_hint",  min: 0,   max: 100000 },
            // Rate limit — the two fields belong together: "at most N messages per W
            // seconds", per sender, across all of their conversations. 0 messages means
            // unlimited (MessageService skips the check), which is why the lower bound
            // here is 0 while the window below starts at 1: a zero-length window is a
            // broken limit, not a disabled one. Ranges mirror NUMERIC_RANGES in
            // SystemSettingService; keep both sides in sync.
            { type: "number", key: SETTING_KEYS.MSG_RATE_LIMIT_MAX_MESSAGES,   labelKey: "admin_settings_msg_rate_limit_max",    hintKey: "admin_settings_msg_rate_limit_max_hint",    min: 0, max: 10000 },
            { type: "number", key: SETTING_KEYS.MSG_RATE_LIMIT_WINDOW_SECONDS, labelKey: "admin_settings_msg_rate_limit_window", hintKey: "admin_settings_msg_rate_limit_window_hint", min: 1, max: 3600 },
        ],
    },
    {
        id:       "search",
        icon:     <FaSearch />,
        titleKey: "admin_settings_group_search",
        keys: [
            SETTING_KEYS.SEARCH_TYPEAHEAD_MIN_CHARS,
            SETTING_KEYS.SEARCH_TYPEAHEAD_DELAY_MS,
            SETTING_KEYS.SEARCH_RESULT_LIMIT,
        ],
        fields: [
            { type: "number", key: SETTING_KEYS.SEARCH_TYPEAHEAD_MIN_CHARS, labelKey: "admin_settings_search_min_chars",    hintKey: "admin_settings_search_min_chars_hint",    min: 1, max: 10 },
            { type: "number", key: SETTING_KEYS.SEARCH_TYPEAHEAD_DELAY_MS,  labelKey: "admin_settings_search_delay_ms",     hintKey: "admin_settings_search_delay_ms_hint",     min: 0, max: 2000 },
            { type: "number", key: SETTING_KEYS.SEARCH_RESULT_LIMIT,        labelKey: "admin_settings_search_result_limit", hintKey: "admin_settings_search_result_limit_hint", min: 1, max: 20 },
        ],
    },
    {
        id:       "media",
        icon:     <FaImage />,
        titleKey: "admin_settings_group_media",
        keys: [
            SETTING_KEYS.MEDIA_AVATAR_MAX_MB,
            SETTING_KEYS.MEDIA_BANNER_MAX_MB,
            SETTING_KEYS.MEDIA_ENTRY_IMAGE_MAX_MB,
        ],
        fields: [
            { type: "number", key: SETTING_KEYS.MEDIA_AVATAR_MAX_MB,      labelKey: "admin_settings_media_avatar_max_mb",      hintKey: "admin_settings_media_avatar_max_mb_hint",      min: 1, max: 20 },
            { type: "number", key: SETTING_KEYS.MEDIA_BANNER_MAX_MB,      labelKey: "admin_settings_media_banner_max_mb",      hintKey: "admin_settings_media_banner_max_mb_hint",      min: 1, max: 20 },
            { type: "number", key: SETTING_KEYS.MEDIA_ENTRY_IMAGE_MAX_MB, labelKey: "admin_settings_media_entry_image_max_mb", hintKey: "admin_settings_media_entry_image_max_mb_hint", min: 1, max: 20 },
        ],
    },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const AdminSettings: React.FC<AdminSettingsProps> = ({ isAdmin }) => {
    const { t } = useTranslation();
    // Used to refresh the header immediately after saving site name / slogan
    const { refresh: refreshSiteSettings } = useSiteSettings();

    // Flat map of all setting values — key → raw string value
    const [values, setValues]   = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);

    // Per-group saving state: groupId → "idle" | "saving" | "saved"
    const [groupState, setGroupState] = useState<Record<string, "idle" | "saving" | "saved">>({});

    // Per-group save error: groupId → message. Kept separate from the page-level
    // banner because that banner sits above the first group — an admin working on
    // a group further down the page never saw it and read the refused save as
    // "nothing happened".
    const [groupError, setGroupError] = useState<Record<string, string | null>>({});

    // Load all settings on mount
    useEffect(() => {
        setLoading(true);
        getAllSettings()
            .then((data: SystemSettingDto[]) => {
                setValues(toValueMap(data));
                setError(null);
            })
            .catch(() => setError(t("admin_settings_load_error")))
            .finally(() => setLoading(false));
    }, [t]);

    // Update a single field value in local state.
    // Editing anything clears the save error of the group the field belongs to,
    // so a stale "must be between ..." message never lingers over a fixed value.
    const handleChange = useCallback((key: string, newValue: string) => {
        setValues(prev => ({ ...prev, [key]: newValue }));
        const owner = GROUPS.find(g => g.keys.includes(key));
        if (owner) {
            setGroupError(prev => (prev[owner.id] ? { ...prev, [owner.id]: null } : prev));
        }
    }, []);

    // Save a specific group
    const handleSave = useCallback(async (group: GroupDef) => {
        // Client-side range check. The backend rejects out-of-range values too,
        // but a number input lets an admin type anything, and before this the
        // value was silently clamped on read — the panel reported success while
        // storing something the app never used.
        const invalid = group.fields.find(f => {
            if (f.type !== "number") return false;
            const raw = values[f.key];
            if (raw === undefined) return false;
            const num = Number(raw);
            if (raw.trim() === "" || isNaN(num)) return true;
            if (f.min !== undefined && num < f.min) return true;
            if (f.max !== undefined && num > f.max) return true;
            return false;
        }) as InputFieldDef | undefined;

        if (invalid) {
            setGroupError(prev => ({
                ...prev,
                [group.id]: t("admin_settings_range_error", {
                    field: t(invalid.labelKey),
                    min: invalid.min,
                    max: invalid.max,
                }),
            }));
            return;
        }

        const patch: Record<string, string> = {};
        group.keys.forEach(k => {
            if (values[k] !== undefined) patch[k] = values[k];
        });

        setGroupState(prev => ({ ...prev, [group.id]: "saving" }));
        setError(null);
        setGroupError(prev => ({ ...prev, [group.id]: null }));

        try {
            const updated = await batchUpdateSettings(patch);
            // Merge updated values back into local state
            setValues(prev => ({ ...prev, ...toValueMap(updated) }));
            setGroupState(prev => ({ ...prev, [group.id]: "saved" }));

            // If the "general" group was saved, refresh the header context immediately
            if (group.id === "general") {
                await refreshSiteSettings();
            }

            // Reset to idle after 2.5 s
            setTimeout(() => {
                setGroupState(prev => ({ ...prev, [group.id]: "idle" }));
            }, 2500);
        } catch (err) {
            setGroupState(prev => ({ ...prev, [group.id]: "idle" }));
            // A SETTING_OUT_OF_RANGE rejection names the offending key and its
            // bounds — showing it beats a generic "could not save"
            const message = err instanceof Error ? err.message : "";
            setGroupError(prev => ({
                ...prev,
                [group.id]: parseApiError(message, t, "admin_settings_save_error"),
            }));
        }
    }, [values, t, refreshSiteSettings]);

    // ── Render helpers ────────────────────────────────────────────

    const renderField = (field: FieldDef) => {
        const value = values[field.key] ?? "";

        if (field.type === "toggle") {
            const checked = value === "true";
            return (
                <div key={field.key} className="settings-field">
                    <div className="settings-toggle-row">
                        <div className="settings-toggle-label">
                            <span>{t(field.labelKey)}</span>
                            {field.hintKey && (
                                <p className="settings-field-hint">{t(field.hintKey)}</p>
                            )}
                        </div>
                        <label className="settings-toggle">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={e =>
                                    handleChange(field.key, e.target.checked ? "true" : "false")
                                }
                            />
                            <span className="settings-toggle-slider" />
                        </label>
                    </div>
                </div>
            );
        }

        if (field.type === "select") {
            return (
                <div key={field.key} className="settings-field">
                    <label>{t(field.labelKey)}</label>
                    <select
                        value={value}
                        onChange={e => handleChange(field.key, e.target.value)}
                    >
                        {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {field.hintKey && (
                        <p className="settings-field-hint">{t(field.hintKey)}</p>
                    )}
                </div>
            );
        }

        // text or number input
        const isNumber = field.type === "number";
        const isFull   = (field as InputFieldDef).full;

        return (
            <div
                key={field.key}
                className={`settings-field${isFull ? " settings-field-full" : ""}`}
            >
                <label>{t(field.labelKey)}</label>
                {field.key === SETTING_KEYS.SITE_ANNOUNCEMENT_TEXT ? (
                    <textarea
                        value={value}
                        onChange={e => handleChange(field.key, e.target.value)}
                        rows={3}
                        maxLength={(field as InputFieldDef).maxLength ?? 500}
                    />
                ) : (
                    <input
                        type={field.type}
                        value={value}
                        min={isNumber ? (field as InputFieldDef).min : undefined}
                        max={isNumber ? (field as InputFieldDef).max : undefined}
                        maxLength={!isNumber ? (field as InputFieldDef).maxLength : undefined}
                        onChange={e => handleChange(field.key, e.target.value)}
                    />
                )}
                {/* Hint on the left, live character counter on the right — one
                    row, so a capped field costs no extra vertical space. */}
                <div className="settings-field-meta">
                    {field.hintKey ? (
                        <p className="settings-field-hint">{t(field.hintKey)}</p>
                    ) : <span />}
                    {!isNumber && (field as InputFieldDef).maxLength && (
                        <span className={`settings-field-counter${
                            String(value).length >= (field as InputFieldDef).maxLength! ? " at-limit" : ""
                        }`}>
                            {String(value).length} / {(field as InputFieldDef).maxLength}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderGroup = (group: GroupDef) => {
        const state    = groupState[group.id] ?? "idle";
        const saveError = groupError[group.id] ?? null;

        return (
            <div key={group.id} className="settings-group">
                {/* Group header */}
                <div className="settings-group-header">
                    <span className="settings-group-icon">{group.icon}</span>
                    <h3 className="settings-group-title">{t(group.titleKey)}</h3>
                </div>

                {/* Fields */}
                <div className="settings-fields">
                    {group.fields.map(renderField)}
                </div>

                {/* Footer with save button */}
                <div className="settings-group-footer">
                    {saveError && (
                        <span className="settings-save-error">
                            <FaExclamationTriangle /> {saveError}
                        </span>
                    )}
                    {state === "saved" && (
                        <span className="settings-save-success">
                            <FaCheck /> {t("admin_settings_saved")}
                        </span>
                    )}
                    <button
                        className="settings-save-btn"
                        onClick={() => handleSave(group)}
                        disabled={state === "saving"}
                    >
                        {state === "saving" ? (
                            <>
                                <FaSpinner className="fa-spin" />
                                {t("admin_settings_saving")}
                            </>
                        ) : (
                            <>
                                <FaSave />
                                {t("admin_settings_save_group")}
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    // ── Main render ───────────────────────────────────────────────

    if (loading) {
        return (
            <div className="settings-loading">
                <FaSpinner className="fa-spin" />
                {t("admin_settings_loading")}
            </div>
        );
    }

    return (
        <div className="admin-settings">
            <AdminErrorBanner message={error} />
            {GROUPS.map(renderGroup)}
            {/* Background job scheduling — its own component because a cron
                expression needs a preset picker, a validity check and a next-run
                preview, none of which fit the generic key/value field grid above.
                ADMIN only: the endpoints behind it reject moderators. */}
            {isAdmin && <AdminSchedulers />}
        </div>
    );
};